import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/upsell/api/index.api';
import {
  DepositAccountGetDetails,
  DepositAccountUpdateKYC,
  DepositAccountGetSigningURL,
  DepositAccountGet,
} from '@modules/store/upsell/types/UpsellAPI.interface';
import UpsellDepositAccountState from '@modules/store/upsell/types/UpsellDepositAccountState.entity';
import { AppRole, AppRoleEnum } from '@modules/husleie-framework/src/types/App.entity';
import { UpsellKYCErrorEnum, UpsellStatusEnum } from '@modules/husleie-framework/src/types/Upsell.entity';
import { UpsellDepositAccount } from '@modules/husleie-framework/src/types/UpsellDepositAccount.entity';
import { ActionTree, Commit } from 'vuex';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';

let loadingCount = 0;

function PreAPICall(commit: Commit, role?: AppRole): void {
  commit('common/staticData/setLoading', true, { root: true });
  commit('setUpsellDepositAccountLoading', true);
  commit('common/staticData/setApiError', { error: false, role }, { root: true });
  commit('setUpsellDepositAccountApiError', false);
  loadingCount++;
}

function postAPICall(commit: Commit): void {
  loadingCount--;
  if (loadingCount === 0) {
    commit('common/staticData/setLoading', false, { root: true });
    commit('setUpsellDepositAccountLoading', false);
  }
}

function errorAPICall(commit: Commit, method: string, message: string, role?: AppRole): void {
  commit('common/staticData/setApiError', { error: true, role }, { root: true });
  commit('setUpsellDepositAccountApiError', true);
  LogOperations.error(`Upsell|DepositAccount|Actions|${method}`, message);
}

export const actions: ActionTree<UpsellDepositAccountState, CommonState> = {
  fetchDepositAccountLegacy: ({ commit }, get: DepositAccountGet) => {
    PreAPICall(commit, get.role);
    return new api()
      .getDepositAccountLegacy(get)
      .then((data: any) => {
        commit('setUpsellDepositAccount', data.upsellDepositAccount);
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchDepositAccountLegacy', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  fetchDepositAccount: ({ commit }, get: DepositAccountGet) => {
    PreAPICall(commit, get.role);
    return new api()
      .getDepositAccount(get)
      .then((data: any) => {
        commit('setUpsellDepositAccount', data.upsellDepositAccount);
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchDepositAccountLegacy', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  fetchDepositAccountDetails: ({ commit }, get: DepositAccountGetDetails) => {
    PreAPICall(commit, get.role);
    return new api()
      .getDepositAccountDetails(get)
      .then((data: any) => {
        const upsellDepositAccount: UpsellDepositAccount = {
          uuid: data.uuid,
          kyc: data.kyc,
        };
        commit('setUpsellDepositAccount', upsellDepositAccount);
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchDepositAccountDetails', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  updateDepositAccountKYC: ({ commit }, update: DepositAccountUpdateKYC) => {
    PreAPICall(commit, update.role);
    commit('setUpsellDepositAccount', { kycError: undefined });
    return new api()
      .updateDepositAccountKYC(update)
      .catch((error: any) => {
        const message = error.response?.data?.errors?.[0]?.message;
        if (message?.includes('Manual review required')) {
          commit('setUpsellDepositAccount', {
            status: UpsellStatusEnum.MANUAL,
            kycError: UpsellKYCErrorEnum.MANUAL_REVIEW,
          });
        } else if (message?.includes('Customer failed identity verification')) {
          commit('setUpsellDepositAccount', { kycError: UpsellKYCErrorEnum.VERIFICATION_FAILED });
        } else {
          errorAPICall(commit, 'updateDepositAccountKYC', error.message, update.role);
          throw new Error(error);
        }
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  fetchDepositAccountSigningURL: ({ commit }, get: DepositAccountGetSigningURL) => {
    PreAPICall(commit, get.role);
    return new api()
      .getDepositAccountSigningURL(get)
      .then((data: any) => {
        if (data.signingURL) {
          commit('setUpsellDepositAccountSigningURL', data.signingURL);
        } else if (data.redirectURL) {
          commit('setUpsellDepositAccountRedirectURL', data.redirectURL);
        }
      })
      .catch((error: any) => {
        errorAPICall(commit, 'fetchDepositAccountSigning-RedirectURL', error.message, get.role);
        throw new Error(error);
      })
      .finally(() => {
        postAPICall(commit);
      });
  },

  assignDepositAccountOpportunity: async ({ commit, dispatch }, { depositAccountOpportunity, signingUuid }) => {
    commit('setUpsellDepositAccountUuid', depositAccountOpportunity.uuid);

    // Initial opportunity status, will be overwritten when the 'open' upsell is fetched
    commit('setUpsellDepositAccountStatus', depositAccountOpportunity.status);
    if (depositAccountOpportunity.status === UpsellStatusEnum.OPEN) {
      if (depositAccountOpportunity.uuid) {
        await dispatch('fetchDepositAccountLegacy', { uuid: depositAccountOpportunity.uuid, role: AppRoleEnum.TENANT });
      } else {
        await dispatch('fetchDepositAccountDetails', { uuid: signingUuid, role: AppRoleEnum.TENANT });
      }
    }
    commit('setUpsellDepositAccountLoaded', true);
  },
};
